/**
 * Routeur minimal, adossé à l'API History.
 *
 * Pourquoi pas une bibliothèque de routage : le budget de performance de
 * `DESIGN.md` vise ~50 Ko pour toute la page, polices comprises. Un routeur
 * généraliste en consomme une part notable pour six routes sans imbrication ni
 * chargement différé. Le jour où l'application en aura besoin, cette décision se
 * révise — elle tient en un fichier.
 */

import type { AnchorHTMLAttributes, TargetedMouseEvent } from 'preact';
import { useCallback, useEffect, useState } from 'react';

/** Émis à chaque navigation interne : `popstate` ne couvre pas `pushState`. */
const EVENEMENT_NAVIGATION = 'sygap:navigation';

export function naviguer(chemin: string, options: { remplacer?: boolean } = {}): void {
  if (options.remplacer === true) {
    globalThis.history.replaceState({}, '', chemin);
  } else {
    globalThis.history.pushState({}, '', chemin);
  }
  globalThis.dispatchEvent(new Event(EVENEMENT_NAVIGATION));
}

/** Chemin courant, réévalué à chaque navigation avant ou arrière. */
export function useChemin(): string {
  const [chemin, setChemin] = useState(() => globalThis.location.pathname);

  useEffect(() => {
    const rafraichir = (): void => {
      setChemin(globalThis.location.pathname);
    };
    globalThis.addEventListener('popstate', rafraichir);
    globalThis.addEventListener(EVENEMENT_NAVIGATION, rafraichir);
    return () => {
      globalThis.removeEventListener('popstate', rafraichir);
      globalThis.removeEventListener(EVENEMENT_NAVIGATION, rafraichir);
    };
  }, []);

  return chemin;
}

type ProprietesLien = AnchorHTMLAttributes & { href: string };

/**
 * Lien interne. Reste une vraie balise `<a>` : clic milieu, « ouvrir dans un
 * nouvel onglet » et navigation clavier continuent de fonctionner, ce qu'un
 * `<div onClick>` casserait.
 */
export function Lien({ href, onClick, children, ...reste }: ProprietesLien) {
  const intercepter = useCallback(
    (evenement: TargetedMouseEvent<HTMLAnchorElement>) => {
      onClick?.(evenement);
      const modificateur =
        evenement.metaKey || evenement.ctrlKey || evenement.shiftKey || evenement.altKey;
      if (evenement.defaultPrevented || modificateur || evenement.button !== 0) {
        return;
      }
      evenement.preventDefault();
      naviguer(href);
    },
    [href, onClick],
  );

  return (
    <a href={href} onClick={intercepter} {...reste}>
      {children}
    </a>
  );
}

/**
 * Extrait les segments d'un chemin, vides retirés.
 * `/dossiers/42` → `['dossiers', '42']`
 */
export function segmenter(chemin: string): readonly string[] {
  return chemin.split('/').filter((segment) => segment !== '');
}
